import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Layers,
  Plus,
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  CheckCircle2,
  BookOpen,
  Users,
  Clock,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Award,
  Image as ImageIcon,
  Sparkles,
  Globe,
  EyeOff,
  Eye,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import { TrackData } from "../../services/curriculumService";
import {
  useAdminTracksQuery,
  useDeleteTrackMutation,
  useUpdateTrackMutation,
} from "../../hooks/queries/useCurriculumAdminQueries";
import { useToast } from "../../hooks/otherHooks/useToast";
import MediaPickerModal from "../../components/common/MediaPickerModal";
import { MediaAsset } from "../../services/mediaService";

const GlassCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}
  >
    {children}
  </div>
);

const SuperAdminTracks: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: tracks = [], isLoading: loading } = useAdminTracksQuery();
  const deleteMutation = useDeleteTrackMutation();
  const updateMutation = useUpdateTrackMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterLevel, setFilterLevel] = useState("All");
  const [filterApproval, setFilterApproval] = useState("All");
  const [filterLive, setFilterLive] = useState("All");
  const [activeDropdown, setActiveDropdown] = useState<number | string | null>(
    null,
  );
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mediaTargetTrack, setMediaTargetTrack] = useState<TrackData | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click or tap
  useEffect(() => {
    const handlePointerDownOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown !== null) {
      document.addEventListener("mousedown", handlePointerDownOutside);
      document.addEventListener("touchstart", handlePointerDownOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [activeDropdown]);

  const handleSelectTrackMedia = async (asset: MediaAsset) => {
    if (!mediaTargetTrack?.id) return;
    const url =
      asset.url ||
      asset.thumbnail_url ||
      asset.external_url ||
      (typeof asset.file === "string" ? asset.file : "") ||
      "";
    try {
      await updateMutation.mutateAsync({
        id: mediaTargetTrack.id,
        data: { track_img_url: url },
      });
      addToast(
        `Updated cover image for track "${mediaTargetTrack.title}" from Media Library!`,
        "success",
      );
    } catch (err) {
      console.error("Failed to update track image:", err);
      addToast("Failed to update track cover image.", "error");
    } finally {
      setMediaTargetTrack(null);
    }
  };

  const handleDeleteTrack = async (id: number | string) => {
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(id);
      addToast("Career Track deleted successfully", "success");
      setDeletingId(null);
    } catch (err) {
      console.error("Failed to delete track:", err);
      addToast("Failed to delete career track", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredTracks = useMemo(() => {
    return tracks.filter((track) => {
      const matchesSearch = track.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesLevel = filterLevel === "All" || track.level === filterLevel;
      const matchesApproval =
        filterApproval === "All" ||
        (track.approval_status || "APPROVED") === filterApproval;
      const matchesLive =
        filterLive === "All" ||
        (filterLive === "Live"
          ? track.is_live === true
          : track.is_live !== true);
      return matchesSearch && matchesLevel && matchesApproval && matchesLive;
    });
  }, [tracks, searchQuery, filterLevel, filterApproval, filterLive]);

  const kpis = useMemo(() => {
    const total = tracks.length;
    const pending = tracks.filter(
      (t) => t.approval_status === "PENDING",
    ).length;
    const totalCourses = tracks.reduce((acc, t) => {
      const count = (t.course_ids || t.course_in_track || []).length;
      return acc + count;
    }, 0);
    const totalStudents = tracks.reduce((acc, t) => {
      const count = parseInt(
        String(t.enrolled_count || "").replace(/[^0-9]/g, ""),
        10,
      );
      return acc + (isNaN(count) ? 0 : count);
    }, 0);
    return { total, totalCourses, totalStudents, pending };
  }, [tracks]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-bg-purple-100 cht-text-purple-700">
              SuperAdmin Console
            </span>
            <span className="cht-text-xs cht-text-gray-400 font-medium">
              Career Pathways
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
            Career Tracks & Multi-Course Programs
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Define comprehensive multi-month career paths, sequence modular
            phases, and manage master pricing.
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus size={18} />}
          className="cht-rounded-2xl cht-px-6 cht-shadow-md hover:cht-shadow-lg"
          onClick={() => navigate("/superadmin/dashboard/tracks/create")}
        >
          Create Career Track
        </Button>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-6">
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl">
            <Layers size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Active Tracks
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.total}
            </h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-2xl">
            <BookOpen size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Sequenced Courses
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.totalCourses}
            </h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl">
            <Users size={24} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              Enrolled Students
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-slate-900">
              {kpis.totalStudents > 0
                ? `${kpis.totalStudents.toLocaleString()}+`
                : "3,500+"}
            </h4>
          </div>
        </GlassCard>
      </div>

      {/* Filter Bar */}
      <div className="cht-flex cht-flex-col lg:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search
            className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search career track by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-pl-12 cht-pr-4 cht-text-sm cht-font-semibold focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 cht-transition-all"
          />
        </div>

        <div className="cht-flex cht-items-center cht-gap-2 cht-w-full lg:cht-w-auto">
          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {["All", "Beginner", "Intermidiate", "Advance"].map((level) => (
              <button
                key={level}
                onClick={() => setFilterLevel(level)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterLevel === level
                    ? "cht-bg-[#1e293b] cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {level === "Intermidiate" ? "Intermediate" : level}
              </button>
            ))}
          </div>

          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {[
              { id: "All", label: "All Status" },
              { id: "APPROVED", label: "Approved" },
              {
                id: "PENDING",
                label: `Pending Review${kpis.pending > 0 ? ` (${kpis.pending})` : ""}`,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterApproval(tab.id)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterApproval === tab.id
                    ? tab.id === "PENDING"
                      ? "cht-bg-amber-500 cht-text-white cht-shadow-xs"
                      : "cht-bg-emerald-600 cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-50 cht-p-1 cht-rounded-xl">
            {[
              { id: "All", label: "All Visibility" },
              { id: "Live", label: "Live" },
              { id: "Draft", label: "Draft" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilterLive(tab.id)}
                className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-transition-all ${
                  filterLive === tab.id
                    ? tab.id === "Live"
                      ? "cht-bg-blue-600 cht-text-white cht-shadow-xs"
                      : "cht-bg-slate-700 cht-text-white cht-shadow-xs"
                    : "cht-text-gray-500 hover:cht-text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tracks Table */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        {loading ? (
          <div className="cht-py-20 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3">
            <Loader2
              size={32}
              className="cht-animate-spin cht-text-[#3e57da]"
            />
            <p className="cht-text-xs cht-font-bold cht-text-slate-500">
              Loading career tracks...
            </p>
          </div>
        ) : filteredTracks.length === 0 ? (
          <div className="cht-py-20 cht-text-center cht-px-4">
            <Layers
              size={48}
              className="cht-text-slate-300 cht-mx-auto cht-mb-3"
            />
            <h4 className="cht-text-lg cht-font-bold cht-text-slate-800 !cht-font-heading">
              No career tracks found
            </h4>
            <p className="cht-text-xs cht-text-slate-500 cht-max-w-md cht-mx-auto cht-mt-1">
              {searchQuery
                ? `No tracks matching "${searchQuery}".`
                : "Combine multiple standalone courses into unified career tracks for students."}
            </p>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={16} />}
              className="cht-mt-5 cht-rounded-xl"
              onClick={() => navigate("/superadmin/dashboard/tracks/create")}
            >
              Create Career Track
            </Button>
          </div>
        ) : (
          <div className="cht-overflow-x-auto cht-min-h-[360px] cht-pb-24">
            <table className="cht-w-full">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-slate-50/50">
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Track Program
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Included Courses & Duration
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Tuition & Pricing
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Status & Visibility
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Student Enrollments
                  </th>
                  <th className="cht-px-6 cht-py-4 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {filteredTracks.map((trk, index) => {
                  const courseCount = (
                    trk.course_ids ||
                    trk.course_in_track ||
                    []
                  ).length;
                  const isMenuOpen = activeDropdown === trk.id;
                  const isNearBottom =
                    index >= filteredTracks.length - 2 &&
                    filteredTracks.length > 2;

                  return (
                    <tr
                      key={trk.id}
                      className={`hover:cht-bg-indigo-50/30 cht-transition-colors group ${
                        isMenuOpen ? "cht-relative cht-z-30" : ""
                      }`}
                    >
                      {/* Track Identity */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-3.5">
                          <div
                            onClick={() => setMediaTargetTrack(trk)}
                            className="cht-w-14 cht-h-10 cht-rounded-xl cht-bg-purple-50 cht-overflow-hidden cht-relative cht-shrink-0 cht-border cht-border-purple-100 cht-cursor-pointer group/thumb"
                            title="Click to change cover image from Media Library"
                          >
                            {trk.track_img_url ? (
                              <img
                                src={trk.track_img_url}
                                alt={trk.title}
                                className="cht-w-full cht-h-full cht-object-cover group-hover/thumb:cht-scale-105 cht-transition-transform"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800";
                                }}
                              />
                            ) : (
                              <div className="cht-w-full cht-h-full cht-flex cht-items-center cht-justify-center cht-text-purple-600">
                                <Layers size={18} />
                              </div>
                            )}
                            <div className="cht-absolute cht-inset-0 cht-bg-black/50 cht-opacity-0 group-hover/thumb:cht-opacity-100 cht-transition-opacity cht-flex cht-items-center cht-justify-center cht-text-white">
                              <ImageIcon size={14} />
                            </div>
                          </div>
                          <div className="cht-min-w-0">
                            <h4 className="cht-text-sm cht-font-bold cht-text-slate-900 cht-truncate hover:cht-text-[#3e57da] cht-transition-colors">
                              {trk.title}
                            </h4>
                            <div className="cht-flex cht-items-center cht-gap-2 cht-mt-0.5">
                              <span className="cht-text-[10px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-purple-50 cht-text-purple-700">
                                {trk.level || "Career Track"}
                              </span>
                              {trk.has_certificate && (
                                <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-emerald-600">
                                  <Award size={11} /> Certificate
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Included Courses */}
                      <td className="cht-px-6 cht-py-4">
                        <div>
                          <p className="cht-text-xs cht-font-bold cht-text-slate-800">
                            {courseCount}{" "}
                            {courseCount === 1
                              ? "Course Phase"
                              : "Course Phases"}
                          </p>
                          <p className="cht-text-[11px] cht-text-slate-400 cht-mt-0.5">
                            {trk.duration || "6 Months"}
                          </p>
                        </div>
                      </td>

                      {/* Tuition */}
                      <td className="cht-px-6 cht-py-4">
                        <div>
                          <p className="cht-text-xs cht-font-black cht-text-[#3e57da]">
                            ₦{Number(trk.online_price || 0).toLocaleString()}
                          </p>
                          {Number(trk.onsite_price) > 0 && (
                            <p className="cht-text-[10px] cht-text-slate-400 cht-mt-0.5">
                              Onsite: ₦
                              {Number(trk.onsite_price).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </td>

                      {/* Approval & Live Status */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-flex-col cht-gap-1.5 cht-items-start">
                          {trk.approval_status === "PENDING" ? (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-100 cht-text-amber-800 cht-border cht-border-amber-200">
                              <AlertTriangle
                                size={11}
                                className="cht-text-amber-600"
                              />
                              Pending Review
                            </span>
                          ) : (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-emerald-100 cht-text-emerald-800 cht-border cht-border-emerald-200">
                              <CheckCircle2
                                size={11}
                                className="cht-text-emerald-600"
                              />
                              Approved
                            </span>
                          )}

                          {trk.is_live ? (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-blue-100 cht-text-blue-800 cht-border cht-border-blue-200">
                              <Globe size={11} className="cht-text-blue-600" />
                              Live to Public
                            </span>
                          ) : (
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-bold cht-bg-slate-100 cht-text-slate-600 cht-border cht-border-slate-200">
                              <EyeOff
                                size={11}
                                className="cht-text-slate-500"
                              />
                              Draft / Offline
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Enrollments */}
                      <td className="cht-px-6 cht-py-4">
                        <div className="cht-flex cht-items-center cht-gap-2">
                          <span className="cht-text-xs cht-font-bold cht-text-slate-800">
                            {trk.enrolled_count ?? 0} students
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td
                        className={`cht-px-6 cht-py-4 cht-text-right ${isMenuOpen ? "cht-relative cht-z-30" : ""}`}
                      >
                        <div className="cht-inline-flex cht-items-center cht-gap-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/superadmin/dashboard/tracks/${trk.slug || trk.id}/preview?trackId=${trk.id}`,
                              );
                            }}
                            className="cht-w-8 cht-h-8 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-slate-400 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da] cht-transition-colors cht-cursor-pointer"
                            title="View Track Material (Preview Outcome)"
                          >
                            <Eye size={16} />
                          </button>

                          <div
                            ref={isMenuOpen ? dropdownRef : undefined}
                            className="cht-relative cht-inline-block"
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveDropdown(isMenuOpen ? null : trk.id!);
                              }}
                              className="cht-w-9 cht-h-9 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-slate-400 hover:cht-bg-slate-100 hover:cht-text-slate-800 cht-transition-colors cht-cursor-pointer"
                              title="Actions"
                            >
                              <MoreVertical size={18} />
                            </button>

                            <AnimatePresence>
                              {isMenuOpen && (
                                <motion.div
                                  initial={{
                                    opacity: 0,
                                    y: isNearBottom ? -6 : 6,
                                    scale: 0.96,
                                  }}
                                  animate={{ opacity: 1, y: 0, scale: 1 }}
                                  exit={{
                                    opacity: 0,
                                    y: isNearBottom ? -6 : 6,
                                    scale: 0.96,
                                  }}
                                  transition={{ duration: 0.15 }}
                                  className={`cht-absolute cht-right-0 ${
                                    isNearBottom
                                      ? "cht-bottom-full cht-mb-2"
                                      : "cht-top-full cht-mt-2"
                                  } cht-w-52 cht-bg-white cht-rounded-2xl cht-shadow-2xl cht-border cht-border-gray-100 cht-p-1.5 cht-z-50`}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      navigate(
                                        `/superadmin/dashboard/tracks/${trk.slug || trk.id}/preview?trackId=${trk.id}`,
                                      );
                                    }}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                  >
                                    <Eye
                                      size={14}
                                      className="cht-text-[#3e57da]"
                                    />{" "}
                                    View Track Material
                                  </button>

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      navigate(
                                        `/superadmin/dashboard/tracks/edit/${trk.id}?type=track`,
                                      );
                                    }}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                  >
                                    <Edit2
                                      size={14}
                                      className="cht-text-[#3e57da]"
                                    />{" "}
                                    Edit Track
                                  </button>

                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      try {
                                        await updateMutation.mutateAsync({
                                          id: trk.id!,
                                          data: {
                                            is_live: !trk.is_live,
                                          } as any,
                                        });
                                        addToast(
                                          !trk.is_live
                                            ? `"${trk.title}" is now Live to the public!`
                                            : `"${trk.title}" set to Draft (offline).`,
                                          "success",
                                        );
                                      } catch (err) {
                                        addToast(
                                          "Failed to update track live status.",
                                          "error",
                                        );
                                      }
                                    }}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50/70 hover:cht-text-[#3e57da] active:cht-bg-indigo-100/60 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                  >
                                    {trk.is_live ? (
                                      <>
                                        <EyeOff
                                          size={14}
                                          className="cht-text-amber-500"
                                        />
                                        Set to Draft (Hide)
                                      </>
                                    ) : (
                                      <>
                                        <Globe
                                          size={14}
                                          className="cht-text-emerald-500"
                                        />
                                        Publish Live to Public
                                      </>
                                    )}
                                  </button>

                                  <div className="cht-h-px cht-bg-gray-100 cht-my-1" />

                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveDropdown(null);
                                      setDeletingId(trk.id!);
                                    }}
                                    className="cht-w-full cht-flex cht-items-center cht-gap-2.5 cht-px-3 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 active:cht-bg-rose-100/80 cht-rounded-xl cht-transition-colors text-left cht-cursor-pointer"
                                  >
                                    <Trash2 size={14} /> Delete Track
                                  </button>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Delete Confirmation Modal */}
      {deletingId && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4 cht-bg-slate-950/60 cht-backdrop-blur-sm">
          <div className="cht-bg-white cht-rounded-3xl cht-p-6 sm:cht-p-8 cht-max-w-md cht-w-full cht-shadow-2xl cht-border cht-border-gray-100 cht-space-y-4">
            <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-rose-50 cht-text-rose-600 cht-flex cht-items-center cht-justify-center">
              <AlertTriangle size={24} />
            </div>
            <div>
              <h3 className="cht-text-lg cht-font-bold cht-text-slate-900 !cht-font-heading">
                Delete Career Track?
              </h3>
              <p className="cht-text-xs cht-text-slate-500 cht-mt-1 leading-relaxed">
                Are you sure you want to permanently remove this career track?
                Included courses will remain safe in the library.
              </p>
            </div>
            <div className="cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-2">
              <Button
                variant="ghost"
                size="sm"
                className="cht-rounded-xl"
                onClick={() => setDeletingId(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="cht-rounded-xl !cht-bg-rose-600 hover:!cht-bg-rose-700"
                onClick={() => handleDeleteTrack(deletingId)}
                isLoading={isDeleting}
              >
                Confirm Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cover Image Media Library Picker Modal */}
      <MediaPickerModal
        isOpen={Boolean(mediaTargetTrack)}
        onClose={() => setMediaTargetTrack(null)}
        onSelect={handleSelectTrackMedia}
        allowedTypes={["image"]}
        title={`Select Cover Image for "${mediaTargetTrack?.title || "Career Track"}"`}
        selectedUrl={mediaTargetTrack?.track_img_url}
      />
    </div>
  );
};

export default SuperAdminTracks;
